import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import AINormalInput from '../../../ui/inputs/AINormalInput';
import '../../../styles/pages/notifications/InsightItemConfig.scss';
import MainSquareButton from '../../../ui/buttons/MainSquareButton';
import AITextArea from '../../../ui/inputs/AITextArea';
import { update_custom_prompt_api_call } from '../../../lib/api/custom-prompts/custom-prompts-api-call';
import {
  CustomPrompt,
  CustomPromptUpdateRequest,
} from '../../../interfaces/CustomPrompt';
import SettingsIconSelectInput from '../../../ui/inputs/SettingsIconSelectInput';
import ToggleSwitch from '../../../ui/buttons/ToggleSwitch';
import { RootState } from '../../../store/store';
import CustomSVGs from '../../../ui/customSVGs/CustomSVGs';
import { infoSVG } from '../../../lib/allCustomSVGs';
import Popup from '../../../ui/popup/Popup';

type Props = {
  togglePopupModal: (isOpen: boolean) => void;
  onCustomPromptUpdated?: () => void;
  customPrompt: CustomPrompt;
};

const EditReportsCustomPrompts = ({
  togglePopupModal,
  onCustomPromptUpdated,
  customPrompt,
}: Props) => {
  const { t } = useTranslation();
  const userProfile = useSelector((state: RootState) => state.user.profile);
  const isAgencyUser = userProfile?.role === 'agency';
  const isMasterPrompt = Boolean(customPrompt.is_master);
  const initialSyncState = customPrompt.is_in_sync_group ?? false;

  const [isSaveDisabled, setIsSaveDisabled] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isConfirmingUnlink, setIsConfirmingUnlink] = useState(false);

  const [promptName, setPromptName] = useState(customPrompt.name);
  const [promptText, setPromptText] = useState(customPrompt.prompt);
  const [outputType, setOutputType] = useState(
    customPrompt.output_type?.presentation ?? 'text'
  );
  const [syncAcrossAgency, setSyncAcrossAgency] = useState(initialSyncState);
  const isToggleDisabled = isAgencyUser && !isMasterPrompt;
  const closeConfirmModal = () => setIsConfirmingUnlink(false);

  const outputTypeOptions = [
    { text: t('Text'), value: 'text' },
    { text: t('Graph'), value: 'graph' },
    { text: t('Table'), value: 'table' },
  ];

  useEffect(() => {
    const isValid = promptName.trim() !== '' && promptText.trim() !== '';
    setIsSaveDisabled(!isValid);
  }, [promptName, promptText]);

  const handleSave = async () => {
    if (isSaveDisabled || isSubmitting) return;

    if (initialSyncState && !syncAcrossAgency) {
      setIsConfirmingUnlink(true);
      return;
    }

    setIsSubmitting(true);
    try {
      const customPromptData: CustomPromptUpdateRequest = {
        name: promptName.trim(),
        prompt: promptText.trim(),
        output_type: {
          presentation: outputType,
        },
        is_active: true,
        ...(isAgencyUser && isMasterPrompt
          ? { sync_across_agency: syncAcrossAgency }
          : {}),
      };

      const result = await update_custom_prompt_api_call(
        customPrompt.id,
        customPromptData
      );

      if (result) {
        togglePopupModal(false);
        if (onCustomPromptUpdated) {
          onCustomPromptUpdated();
        }
      }
    } catch (error) {
      console.error('Error updating custom prompt:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleConfirmUnlink = async () => {
    setIsConfirmingUnlink(false);
    setIsSubmitting(true);
    try {
      const customPromptData: CustomPromptUpdateRequest = {
        name: promptName.trim(),
        prompt: promptText.trim(),
        output_type: {
          presentation: outputType,
        },
        is_active: true,
        ...(isAgencyUser && isMasterPrompt
          ? { sync_across_agency: syncAcrossAgency }
          : {}),
      };

      const result = await update_custom_prompt_api_call(
        customPrompt.id,
        customPromptData
      );

      if (result) {
        togglePopupModal(false);
        if (onCustomPromptUpdated) {
          onCustomPromptUpdated();
        }
      }
    } catch (error) {
      console.error('Error updating custom prompt:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className='insightItemConfig'>
      <div className='insightItemConfig__title'>
        <div className='insightItemConfig__title--main'>
          <h3>{t('EditCustomPrompt')}</h3>
        </div>

        <p>{t('UpdateCustomAIPromptForAnalysis')}</p>
      </div>

      <div className='insightItemConfig__inputs'>
        <AINormalInput
          changeListeners={[(e) => setPromptName(e.target.value)]}
          id='promptName'
          value={promptName}
          placeholder={t('PromptName')}
          required={true}
          type='text'
          innerPlaceholder={t('EnterPromptName')}
        />
        <AITextArea
          changeListeners={[(e) => setPromptText(e.target.value)]}
          id='promptText'
          value={promptText}
          placeholder={t('PromptText')}
          required={true}
          innerPlaceholder={t('EnterYourCustomPrompt')}
        />
        <SettingsIconSelectInput
          filterOptions={outputTypeOptions}
          marginTop='0.5rem'
          value={outputType}
          title={t('OutputType')}
          onFilterChange={(val: string) => setOutputType(val)}
        />
        {isAgencyUser && (
          <div className='insightItemConfig__toggle'>
            <ToggleSwitch
              title={t('SyncAcrossAgency')}
              titleAlignment='left'
              isOn={syncAcrossAgency}
              onClick={(checked: boolean) => setSyncAcrossAgency(checked)}
              disabled={isToggleDisabled}
            />
            {isToggleDisabled && (
              <div className='insightItemConfig__toggle-info'>
                <CustomSVGs svg={infoSVG} />
                <span>{t('SyncAcrossAgencyDisabledTooltip')}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className='insightItemConfig__buttons'>
        <MainSquareButton
          value={t('Cancel')}
          type='button'
          buttonType='white'
          disabled={isSubmitting}
          onClick={() => togglePopupModal(false)}
        />
        <MainSquareButton
          value={isSubmitting ? t('Updating...') : t('UpdateCustomPrompt')}
          type='button'
          disabled={isSaveDisabled || isSubmitting}
          onClick={handleSave}
        />
      </div>

      <Popup
        setTrigger={closeConfirmModal}
        trigger={isConfirmingUnlink}
        overlayVariant='blur'
        isCloseBtnHidden
      >
        <div className='insightItemConfig__warning'>
          <h3>{t('ConfirmUnlinkTitle')}</h3>
          <p>
            {t('ConfirmUnlinkMessage', {
              count: userProfile?.all_shops?.length ?? 0,
            })}
          </p>
          <div className='insightItemConfig__warning-buttons'>
            <MainSquareButton
              value={t('Cancel')}
              type='button'
              buttonType='white'
              disabled={isSubmitting}
              onClick={closeConfirmModal}
            />
            <MainSquareButton
              value={t('ProceedAnyway')}
              type='button'
              disabled={isSubmitting}
              buttonType='delete'
              onClick={handleConfirmUnlink}
            />
          </div>
        </div>
      </Popup>
    </div>
  );
};

export default EditReportsCustomPrompts;
